import { Injectable, inject } from '@angular/core';
import { FormSchema } from '@core/form-generator-engine/abstractions/form-schema';
import { Metadata } from '@core/form-generator-engine/abstractions/metadata';
import { PageSchema } from '@core/form-generator-engine/abstractions/page-schema';
import { QuestionSchema } from '@core/form-generator-engine/abstractions/question-schema';
import { SchemaFactory } from '@core/form-generator-engine/abstractions/schema-factory';
import { DynamicForm } from '@core/form-generator-engine/composite-pattern/dynamic-form';
import { Page } from '@core/form-generator-engine/composite-pattern/page';
import { Panel } from '@core/form-generator-engine/composite-pattern/panel';
import { TranslateService } from '@ngx-translate/core';
import { EvaluationResponse } from 'src/app/web-api-client';
import { RulesForm } from '../models/rules-form';
import { RulesFormManager } from './rules-form-manager';
import { SchemaFactoryService } from './schema-factory.service';

@Injectable()
export class FormSchemaFactoryService
  implements SchemaFactory<RulesFormManager>
{
  private readonly translate = inject(TranslateService);
  private readonly schema = inject(SchemaFactoryService);

  build(schemas: RulesFormManager, form?: DynamicForm | undefined): FormSchema {
    const formSchema = this.schema.getFormSchema();
    let pages: PageSchema[] = [];

    if (!form) {
      pages = this.initialize(schemas);
    } else {
      this.update(schemas, form);
    }

    formSchema.elements = pages;
    return formSchema;
  }

  private initialize(ruleFormManager: RulesFormManager): PageSchema[] {
    return ruleFormManager
      .filter((ruleForm: RulesForm) => ruleForm.data !== undefined)
      .map((ruleForm: RulesForm, index: number) => {
        const pageSchema = this.schema.getPage(ruleForm.data!, index);

        if (ruleForm.data!.question && ruleForm.data!.question.type) {
          const panelSchema = this.schema.getPanel(ruleForm.data!, index);
          const question = this.schema.getQuestion(ruleForm.data!);
          this.buildQuestionControls(question, ruleForm.data!);

          panelSchema.elements.push(question);
          pageSchema.elements.push(panelSchema);
        }

        return pageSchema;
      });
  }

  private update(ruleFormManager: RulesFormManager, form: DynamicForm): void {
    ruleFormManager.forEach((ruleForm: RulesForm, index: number) => {
      if (ruleForm.data) {
        const page: Page | undefined = form.findElement((currentPage: Page) =>
          this.findElement(currentPage, ruleForm.data!)
        );
        if (!page) {
          const pageSchema = this.schema.getPage(ruleForm.data!, index);

          if (ruleForm.data!.question && ruleForm.data!.question.type) {
            const panelSchema = this.schema.getPanel(ruleForm.data!, index);
            const question = this.schema.getQuestion(ruleForm.data!);
            this.buildQuestionControls(question, ruleForm.data!);

            panelSchema.elements.push(question);
            pageSchema.elements.push(panelSchema);
          }

          form.addSchemaElement(pageSchema);
        } else {
          this.updatePage(page, ruleForm.data!);
        }
      }
    });
  }

  private buildQuestionControls(
    questionSchema: QuestionSchema,
    { taxPolicyId, callTaxRuleId, question }: EvaluationResponse
  ): void {
    if (question && question.type) {
      const isDisabled = false;
      const control = this.schema.getQuestionControl(
        question.question ?? '',
        question.type ?? 0,
        this.translate.instant('upsertSecurity.answer'),
        {
          taxPolicyId: taxPolicyId ?? 0,
          ruleId: callTaxRuleId ?? 0,
        },
        isDisabled
      );
      questionSchema.elements.push(control);
    }
  }

  private updatePage(page: Page, evaluationResponse: EvaluationResponse): void {
    const panel: Panel | undefined = page.findElement((currentPanel: Panel) =>
      this.findElement(currentPanel, evaluationResponse)
    );

    if (panel) {
      this.updatePanel(panel, evaluationResponse);
    }
  }

  private updatePanel(
    panel: Panel,
    evaluationResponse: EvaluationResponse
  ): void {
    if (evaluationResponse.question && evaluationResponse.question?.name) {
      this.addQuestion(panel, evaluationResponse);
    }
  }

  private addQuestion(
    panel: Panel,
    evaluationResponse: EvaluationResponse
  ): void {
    const newQuestionSchema: QuestionSchema =
      this.schema.getQuestion(evaluationResponse);
    this.buildQuestionControls(newQuestionSchema, evaluationResponse);
    panel.addSchemaElement(newQuestionSchema);
  }

  private findElement(
    element: Metadata,
    evaluationResponse: EvaluationResponse
  ): boolean {
    return (
      evaluationResponse &&
      element.hasMetadata() &&
      (element.metadata('taxPolicyId') as number) ===
        evaluationResponse.taxPolicyId &&
      (element.metadata('ruleId') as number) ===
        evaluationResponse.callTaxRuleId
    );
  }
}
